import React, { useReducer, useEffect, useCallback, useMemo } from 'react';
import { toast } from '../hooks/use-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';
import { TIME_CONSTANTS, durationToSeconds, formatTimeRemaining } from '../utils/timeUtils';
import PhoneScreen from './PhoneScreen';
import LockControls from './LockControls';

// Reducer for managing phone lock state - consolidates related state
const initialState = {
  isLocked: false,
  password: '',
  lockPassword: '',
  lockDuration: TIME_CONSTANTS.DEFAULT_DURATION,
  timeUnit: TIME_CONSTANTS.DEFAULT_TIME_UNIT,
  timeRemaining: 0,
};

const phoneReducer = (state, action) => {
  switch (action.type) {
    case 'SET_PASSWORD':
      return { ...state, password: action.payload };
    case 'SET_LOCK_PASSWORD':
      return { ...state, lockPassword: action.payload };
    case 'SET_LOCK_DURATION':
      return { ...state, lockDuration: action.payload };
    case 'SET_TIME_UNIT':
      return { ...state, timeUnit: action.payload };
    case 'LOCK_PHONE':
      return {
        ...state,
        isLocked: true,
        timeRemaining: action.payload.timeRemaining,
        password: '',
      };
    case 'UNLOCK_PHONE':
      return {
        ...state,
        isLocked: false,
        timeRemaining: 0,
        password: '',
      };
    case 'TICK_TIMER':
      return {
        ...state,
        timeRemaining: Math.max(0, state.timeRemaining - 1),
      };
    case 'CLEAR_PASSWORD':
      return { ...state, password: '' };
    default:
      return state;
  }
};

const PhoneDemo = () => {
  const [state, dispatch] = useReducer(phoneReducer, initialState);
  const { language } = useLanguage();
  const t = useCallback((key) => getTranslation(language, key), [language]);

  const apps = useMemo(() => [
    t('calendar'),
    t('maps'),
    t('mail'),
    t('podcasts'),
    t('camera'),
    t('clock')
  ], [t]);

  // Timer effect - only depends on state flags
  useEffect(() => {
    if (!state.isLocked || state.timeRemaining <= 0) return;

    const timer = setInterval(() => {
      dispatch({ type: 'TICK_TIMER' });
      
      // Check if timer finished
      if (state.timeRemaining <= 1) {
        dispatch({ type: 'UNLOCK_PHONE' });
        toast({
          title: t('unlocked'),
          description: t('unlockedEarly'),
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [state.isLocked, state.timeRemaining, t]);

  const handleLock = useCallback(() => {
    if (!state.lockPassword) {
      toast({
        title: t('passwordRequired'),
        description: t('passwordRequiredDesc'),
        variant: "destructive",
      });
      return;
    }
    if (state.lockDuration < 1) {
      toast({
        title: t('invalidDuration'),
        description: t('invalidDurationDesc'),
        variant: "destructive",
      });
      return;
    }
    
    const totalSeconds = durationToSeconds(state.lockDuration, state.timeUnit);
    dispatch({ 
      type: 'LOCK_PHONE', 
      payload: { timeRemaining: totalSeconds } 
    });
    
    const unitDisplay = state.lockDuration === 1 
      ? t(state.timeUnit.slice(0, -1)) 
      : t(state.timeUnit);
    
    toast({
      title: t('locked'),
      description: `${t('lockedFor')} ${state.lockDuration} ${unitDisplay}. ${t('stayFocused')}`,
    });
  }, [state.lockPassword, state.lockDuration, state.timeUnit, t]);

  const handleUnlock = useCallback(() => {
    if (state.password === state.lockPassword) {
      dispatch({ type: 'UNLOCK_PHONE' });
      toast({
        title: t('unlocked'),
        description: t('unlockedEarly'),
      });
    } else {
      toast({
        title: t('wrongPassword'),
        description: t('wrongPasswordDesc'),
        variant: "destructive",
      });
      dispatch({ type: 'CLEAR_PASSWORD' });
    }
  }, [state.password, state.lockPassword, t]);

  const formatTime = useCallback((seconds) => formatTimeRemaining(seconds), []);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="grid md:grid-cols-2 gap-8 items-start">
        {/* Phone Mockup */}
        <div className="relative">
          <PhoneScreen
            isLocked={state.isLocked}
            timeRemaining={state.timeRemaining}
            password={state.password}
            setPassword={(value) => dispatch({ type: 'SET_PASSWORD', payload: value })}
            handleUnlock={handleUnlock}
            apps={apps}
            t={t}
            formatTime={formatTime}
          />
        </div>

        {/* Control Panel */}
        <LockControls
          isLocked={state.isLocked}
          lockPassword={state.lockPassword}
          setLockPassword={(value) => dispatch({ type: 'SET_LOCK_PASSWORD', payload: value })}
          lockDuration={state.lockDuration}
          setLockDuration={(value) => dispatch({ type: 'SET_LOCK_DURATION', payload: value })}
          timeUnit={state.timeUnit}
          setTimeUnit={(value) => dispatch({ type: 'SET_TIME_UNIT', payload: value })}
          handleLock={handleLock}
          timeRemaining={state.timeRemaining}
          formatTime={formatTime}
          t={t}
        />
      </div>
    </div>
  );
};

export default PhoneDemo;
